import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import AddProject from './AddProject';
import ExistingProjectSearch from '../ExistingProjectWizard/ExistingProjectSearch';
import EditExistingProject from './EditExistingProject';
import ResponseReceivedWizard from '../OperationWizard/ResponseReceivedWizard';
import ProjectAssignedWizard from '../OperationWizard/ProjectAssignedWizard';
import ProjectOngoingWizard from '../OperationWizard/ProjectOngoingWizard';
import ProjectCompleteWizard from '../OperationWizard/ProjectCompleteWizard';
import ProjectApprovedWizardDash from '../OperationWizard/ProjectApprovedWizard/ProjectApprovedWizardDash';
import ProjectRequestOutstandingWizard from '../OperationWizard/ProjectRequestOutstanding/ProjectRequestOutstandingWizard';
import useMatchedProject from '../OperationWizard/useMatchedProject';
import { setOperationProjectStatus, goToStep } from '../../../store/slices/WizardSlice/wizardStepSlice';
import PandLWizardBasePlan from '../OperationWizard/WizardFinance/PandLWizardBasePlan';
import PandLWizardAdvancedPlan from '../OperationWizard/WizardFinance/PandLWizardAdvancedPlan';
import GenerateQuotationWizard from '../OperationWizard/WizardFinance/GenerateQuotationWizard';
import useSubscriptionLevel from '../../../utils/SubscriptionLevel';

const NewProjectDash = () => {
    const dispatch = useDispatch()
    const step = useSelector((state) => state.wizardstep.step);
    const operationProjectStatus = useSelector((state) => state.wizardstep.operationProjectStatus);
    const createdProject = useSelector((state) => state.wizardstep.createdProject);
    const wizardPlanState = useSelector((state) => state.wizardstep.wizardPlanState);
    const wizardProjectState = useSelector((state) => state.wizardstep.wizardProjectState);
    const existingEditAdvancedProject = useSelector((state) => state.wizardstep.existingEditAdvancedProject);
    const subscriptionPackage = useSubscriptionLevel();

    const operationProjectName = createdProject[0]?.ProjectName
    const { SalesStatus } = useMatchedProject(operationProjectName);

    useEffect(() => {
        // Always update operationProjectStatus from SalesStatus when it's available
        // BUT: Don't override if we're on step 4 with "project approved" status (coming from Response Received)
        // This allows the P&L table to be shown when navigating from Response Received
        if (SalesStatus) {
            // Don't override "project approved" status when on step 4 - this is set explicitly when navigating from Response Received
            if (step === 4 && operationProjectStatus === "project approved") {
                // Keep "project approved" status to show P&L table
                return;
            }
            // CRITICAL: Always update if SalesStatus is "response received" to ensure correct wizard is shown
            // This fixes the issue where leads in "response received" bucket show "No Result Found" in ProjectRequestOutstandingWizard
            if (SalesStatus === "response received" && operationProjectStatus !== "response received") {
                dispatch(setOperationProjectStatus("response received"));
                return;
            }
            // Only update if it's different to avoid unnecessary re-renders
            // Note: We read operationProjectStatus from selector but don't include it in deps to avoid infinite loop
            if (operationProjectStatus !== SalesStatus) {
                dispatch(setOperationProjectStatus(SalesStatus));
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [SalesStatus, dispatch, step])

    // Skip P&L Tracker and Quotation pages for Won status leads in ERP package
    useEffect(() => {
        if (wizardPlanState === "advancedplan" && wizardProjectState === "existingProject" && createdProject?.length > 0) {
            const leadStatus = createdProject[0]?.Status?.toLowerCase()?.trim();
            const isWon = leadStatus === "won" || leadStatus === "won recurring";
            
            // If Won status and we're on step 3 or 4, skip to step 5 (EditExistingProject)
            if (isWon && (step === 3 || step === 4)) {
                dispatch(goToStep(5));
            }
        }
    }, [wizardPlanState, wizardProjectState, createdProject, step, dispatch])

    console.log("operationProjectStatus in dash step", step)
    console.log("operationProjectStatus in dash ", operationProjectStatus)
    console.log("operationProjectStatus in createdProject ", createdProject)

    return (
        <div>

            {step === 1 && (
                <>
                    {(wizardPlanState === "basicplan" || wizardPlanState === "advancedplan") && wizardProjectState === "newProject" && (
                        <div><AddProject /> </div>
                    )}
                    {(wizardPlanState === "basicplan" || wizardPlanState === "advancedplan") && wizardProjectState === "existingProject" && (
                        <div><ExistingProjectSearch /> </div>
                    )}
                </>
            )}
            {step === 2 && (
                <div>

                    {((wizardPlanState === "basicplan") && (wizardProjectState === "newProject" || wizardProjectState === "existingProject")) && !existingEditAdvancedProject && (
                        <PandLWizardBasePlan />
                    )}

                    {((wizardPlanState === "basicplan") && wizardProjectState === "existingProject" && existingEditAdvancedProject) && (
                        <div><EditExistingProject /> </div>
                    )}

                    {((wizardPlanState === "advancedplan") && wizardProjectState === "existingProject" && existingEditAdvancedProject) && (
                        <div><EditExistingProject /> </div>
                    )}

                    {(wizardPlanState === "advancedplan" && wizardProjectState === "newProject") && (
                        <ProjectRequestOutstandingWizard />
                    )}

                </div>
            )}

            {step === 3 && (
                <div>
                    {(wizardPlanState === "advancedplan" && wizardProjectState === "existingProject") && (
                        <>
                            {(() => {
                                // Check if lead status is Won - skip both Generate Quotation and P&L Tracker for Won status in ERP
                                const leadStatus = createdProject[0]?.Status?.toLowerCase()?.trim();
                                const isWon = leadStatus === "won" || leadStatus === "won recurring";
                                
                                // If Won status in ERP package, skip this step (useEffect will redirect to step 5)
                                if (isWon) {
                                    return null; // Will be redirected by useEffect
                                }
                                
                                // CRITICAL: Use SalesStatus as the primary source of truth
                                // SalesStatus comes from actual project data and is more reliable than operationProjectStatus
                                // This ensures correct navigation even if operationProjectStatus was reset during back navigation
                                // Always prioritize SalesStatus when available, regardless of subscription package
                                const statusToCheck = SalesStatus || operationProjectStatus || "";
                                
                                console.log("NewProjectDash step 3 - Status check:", {
                                    statusToCheck,
                                    operationProjectStatus,
                                    SalesStatus,
                                    subscriptionPackage,
                                    createdProject: createdProject[0]?.ProjectName
                                });
                                
                                // Normal flow for other statuses
                                if (statusToCheck === "project requested" || statusToCheck === "project request") {
                                    // Double-check: If SalesStatus is "response received", show ResponseReceivedWizard instead
                                    // This handles the case where operationProjectStatus was set incorrectly before SalesStatus loaded
                                    if (SalesStatus === "response received") {
                                        return <ResponseReceivedWizard selectedLeadGroupId={createdProject?.[0]?.groupid?._id || createdProject?.[0]?.groupid || ""} />;
                                    }
                                    return <ProjectRequestOutstandingWizard />;
                                } else if (statusToCheck === "response received") {
                                    return <ResponseReceivedWizard selectedLeadGroupId={createdProject?.[0]?.groupid?._id || createdProject?.[0]?.groupid || ""} />;
                                } else if (statusToCheck === "generate quotation") {
                                    return <GenerateQuotationWizard />;
                                } else if (statusToCheck === "project approved") {
                                    // In ERP module: Skip Project Approved window - it should not appear in wizard flow
                                    if (subscriptionPackage === "ERP" && wizardPlanState === "advancedplan" && wizardProjectState === "existingProject") {
                                        return null; // Do not show Project Approved in ERP wizard
                                    }
                                    return <div><ProjectApprovedWizardDash /> </div>;
                                } else if (statusToCheck === "project assigned") {
                                    return <div><ProjectAssignedWizard /> </div>;
                                } else if (statusToCheck === "project ongoing") {
                                    return <div><ProjectOngoingWizard /> </div>;
                                } else if (statusToCheck === "project complete") {
                                    return <div><ProjectCompleteWizard /> </div>;
                                } else {
                                    // Fallback: Check SalesStatus one more time, or default to ProjectRequestOutstandingWizard
                                    // But if SalesStatus is "response received", show ResponseReceivedWizard instead
                                    if (SalesStatus === "response received") {
                                        return <ResponseReceivedWizard selectedLeadGroupId={createdProject?.[0]?.groupid?._id || createdProject?.[0]?.groupid || ""} />;
                                    }
                                    return <ProjectRequestOutstandingWizard />;
                                }
                            })()}
                        </>
                    )}
                </div>
            )}

            {step === 4 && (
                <div>
                    {(wizardPlanState === "basicplan" && wizardProjectState === "existingProject") && (
                        <PandLWizardBasePlan />
                    )}

                    {((wizardPlanState === "advancedplan") && wizardProjectState === "existingProject") && (
                        <>
                            {(() => {
                                // Check if lead status is Won - skip P&L Tracker for Won status in ERP
                                const leadStatus = createdProject[0]?.Status?.toLowerCase()?.trim();
                                const isWon = leadStatus === "won" || leadStatus === "won recurring";
                                
                                // If Won status in ERP package, skip this step (useEffect will redirect to step 5)
                                if (isWon) {
                                    return null; // Will be redirected by useEffect
                                }
                                
                                // For ERP package: After Response Received with all accepted, show P&L table first, then Generate Quotation
                                if (operationProjectStatus === "generate quotation") {
                                    return <GenerateQuotationWizard />;
                                } else if (operationProjectStatus === "response received") {
                                    // If somehow still on response received, show Response Received Wizard
                                    return <ResponseReceivedWizard selectedLeadGroupId={createdProject?.[0]?.groupid?._id || createdProject?.[0]?.groupid || ""} />;
                                } else if (operationProjectStatus === "project approved") {
                                    // Show P&L table when coming from response received with all accepted (like CRM module)
                                    return <PandLWizardAdvancedPlan />;
                                } else {
                                    // P&L Tracker for other statuses
                                    return <PandLWizardAdvancedPlan />;
                                }
                            })()}
                        </>
                    )}
                </div>
            )}

            {step === 5 && (
                <div>


                    {((wizardPlanState === "advancedplan") && wizardProjectState === "existingProject") && (
                        <div><EditExistingProject /> </div>
                    )}
                    {/* 
                    {operationProjectStatus === "project approved" && (
                        <>
                            {((wizardPlanState === "advancedplan") && wizardProjectState === "existingProject") && (
                                <div><ProjectApprovedWizardDash /> </div>
                            )}
                        </>
                    )} */}
                </div>
            )}
            {step === 6 && (
                <div>
                    {operationProjectStatus === "project approved" && (
                        <>
                            {(() => {
                                // In ERP module: Skip Project Approved window for Won leads
                                if (subscriptionPackage === "ERP" && wizardPlanState === "advancedplan" && wizardProjectState === "existingProject") {
                                    const leadStatus = createdProject[0]?.Status?.toLowerCase()?.trim();
                                    const isWon = leadStatus === "won" || leadStatus === "won recurring";
                                    if (isWon) {
                                        // Redirect to dashboard if somehow we reached here with Won status
                                        return null; // Do not show Project Approved for Won leads in ERP
                                    }
                                }
                                return <div><ProjectApprovedWizardDash /> </div>;
                            })()}
                        </>
                    )}
                </div>
            )}

        </div>
    )
}

export default NewProjectDash
